import { CommonModule } from '@angular/common';

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit
} from '@angular/core';

import { FormsModule } from '@angular/forms';
import { forkJoin, Observable, of, switchMap } from 'rxjs';

import {
  MediaUploadResponse,
  PageHero,
  PageHeroService,
  UpdatePageHeroRequest
} from '../../../../services/page-hero.service';

import { ToastService } from '../../../../services/toast.service';

@Component({
  selector: 'app-admin-home',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule
  ],
  templateUrl: './admin-home.component.html',
  styleUrls: [
    './admin-home.component.css',
    '../../admin-section.shared.css'
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdminHomeComponent implements OnInit, OnDestroy {
  hero: PageHero | null = null;

  eyebrowText = '';
  title = '';
  subtitle = '';
  backgroundType: 'Image' | 'Video' = 'Video';

  mediaAssetId: number | null = null;
  fallbackMediaAssetId: number | null = null;
  defaultVideoMediaAssetId: number | null = null;
  defaultFallbackMediaAssetId: number | null = null;

  backgroundFile: File | null = null;
  fallbackFile: File | null = null;

  backgroundPreviewUrl: string | null = null;
  fallbackPreviewUrl: string | null = null;

  isLoading = true;
  isSaving = false;

  constructor(
    private readonly pageHeroService: PageHeroService,
    private readonly toastService: ToastService,
    private readonly changeDetector: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.loadDefaults();
    this.loadHero();
  }

  ngOnDestroy(): void {
    this.clearBackgroundPreview();
    this.clearFallbackPreview();
  }

  loadHero(): void {
    this.isLoading = true;

    this.pageHeroService.getHero('home').subscribe({
      next: hero => {
        this.hero = hero;

        this.eyebrowText = hero.eyebrowText ?? '';
        this.title = hero.title;
        this.subtitle = hero.subtitle ?? '';
        this.backgroundType = hero.backgroundType;
        this.mediaAssetId = hero.mediaAssetId;
        this.fallbackMediaAssetId = hero.fallbackMediaAssetId;

        this.isLoading = false;

        this.changeDetector.detectChanges();
      },

      error: error => {
        console.error(
          'Unable to load Home page hero:',
          error
        );

        this.isLoading = false;

        this.toastService.error(
          'Unable to load Home page.'
        );

        this.changeDetector.detectChanges();
      }
    });
  }
  loadDefaults(): void {
    this.pageHeroService
      .getHomeDefaults()
      .subscribe({
        next: defaults => {
          this.defaultVideoMediaAssetId =
            defaults.mediaAssetId;

          this.defaultFallbackMediaAssetId =
            defaults.fallbackMediaAssetId;

          this.changeDetector.detectChanges();
        },

        error: error => {
          console.error(
            'Unable to load default Home hero media:',
            error
          );

          this.toastService.error(
            'Unable to load default Home media.'
          );
        }
      });
  }
  backgroundTypeChanged( type: 'Image' | 'Video'): void {
    this.backgroundType = type;

    this.backgroundFile = null;

    this.clearBackgroundPreview();

    if (type === 'Video') {
      this.mediaAssetId =
        this.defaultVideoMediaAssetId;

      this.fallbackFile = null;

      this.clearFallbackPreview();

      this.fallbackMediaAssetId =
        this.defaultFallbackMediaAssetId;
    }
    else {
      /*
      * Don't accidentally try to display the MP4
      * as an image. Wait for the admin to select
      * an image.
      */
      this.mediaAssetId = null;
    }

    this.changeDetector.detectChanges();
  }
  
  saveHero(): void {
    if (this.isSaving) return;

    if (this.backgroundFile && !this.checkFileSize(this.backgroundFile, this.backgroundType === 'Video')) return;
    if (this.backgroundType === 'Video' && this.fallbackFile && !this.checkFileSize(this.fallbackFile, false)) return;

    if (!this.title.trim()) {
      this.toastService.error(
        'Hero title is required.'
      );

      return;
    }

    if (
      this.backgroundFile &&
      !this.isValidBackgroundFile(this.backgroundFile)
    ) {
      this.toastService.error(
        this.backgroundType === 'Video'
          ? 'Video backgrounds must be MP4 files.'
          : 'Background images must be JPEG, PNG or WebP.'
      );

      return;
    }

    this.isSaving = true;

    this.changeDetector.detectChanges();

    const backgroundUpload =
      this.backgroundFile
        ? this.pageHeroService.uploadMedia(
            this.backgroundFile
          )
        : of(null);

    const fallbackUpload =
      this.backgroundType === 'Video' &&
      this.fallbackFile
        ? this.pageHeroService.uploadMedia(
            this.fallbackFile
          )
        : of(null);

    forkJoin({
      background: backgroundUpload,
      fallback: fallbackUpload
    })
      .pipe(
        switchMap(result => {
          const backgroundMediaId =
            result.background?.id ??
            this.mediaAssetId;

          const fallbackMediaId =
            this.backgroundType === 'Video'
              ? (
                  result.fallback?.id ??
                  this.fallbackMediaAssetId
                )
              : null;

          const request: UpdatePageHeroRequest = {
            eyebrowText:
              this.eyebrowText.trim() || null,

            title:
              this.title.trim(),

            accentText:
              null,

            subtitle:
              this.subtitle.trim() || null,

            backgroundType:
              this.backgroundType === 'Video'
                ? 1
                : 0,

            mediaAssetId:
              backgroundMediaId,

            fallbackMediaAssetId:
              fallbackMediaId
          };

          return this.pageHeroService
            .updateHero('home', request);
        })
      )
      .subscribe({
        next: hero => {
          this.hero = hero;

          this.mediaAssetId =
            hero.mediaAssetId;

          this.fallbackMediaAssetId =
            hero.fallbackMediaAssetId;

          this.backgroundFile = null;
          this.fallbackFile = null;

          this.clearBackgroundPreview();
          this.clearFallbackPreview();

          this.isSaving = false;

          this.toastService.success(
            'Home page saved.'
          );

          this.changeDetector.detectChanges();
        },

        error: error => {
          console.error(
            'Unable to save Home page:',
            error
          );

          this.isSaving = false;

          this.toastService.error(
            typeof error.error === 'string' && error.error.trim()
              ? error.error
              : error.error?.message ?? (error.status === 413
                ? 'File is too large. Images must be 5 MB or smaller; videos must be 25 MB or smaller.'
                : 'Unable to save Home page.')
          );

          this.changeDetector.detectChanges();
        }
      });
  }

  backgroundSelected(event: Event): void {
    const input =
      event.target as HTMLInputElement;

    const file =
      input.files?.[0];

    input.value = '';

    if (!file) {
      return;
    }

    if (!this.isValidBackgroundFile(file)) {
      this.toastService.error(
        this.backgroundType === 'Video'
          ? 'Video backgrounds must be MP4 files.'
          : 'Background images must be JPEG, PNG or WebP.'
      );

      return;
    }

    if (!this.checkFileSize(file, this.backgroundType === 'Video')) return;

    this.clearBackgroundPreview();

    this.backgroundFile = file;

    this.backgroundPreviewUrl =
      URL.createObjectURL(file);

    this.changeDetector.detectChanges();
  }

  fallbackSelected(event: Event): void {
    const input =
      event.target as HTMLInputElement;

    const file =
      input.files?.[0];

    input.value = '';

    if (!file) {
      return;
    }

    if (!this.isValidImage(file)) {
      this.toastService.error(
        'Fallback image must be JPEG, PNG or WebP.'
      );

      return;
    }

    if (!this.checkFileSize(file, false)) return;

    this.clearFallbackPreview();

    this.fallbackFile = file;

    this.fallbackPreviewUrl =
      URL.createObjectURL(file);

    this.changeDetector.detectChanges();
  }

  removeBackground(): void {
    this.backgroundFile = null;
    this.mediaAssetId = null;

    this.clearBackgroundPreview();

    this.changeDetector.detectChanges();
  }

  removeFallback(): void {
    this.fallbackFile = null;
    this.fallbackMediaAssetId = null;

    this.clearFallbackPreview();

    this.changeDetector.detectChanges();
  }

  get backgroundUrl(): string | null {
    if (this.backgroundPreviewUrl) {
      return this.backgroundPreviewUrl;
    }

    return this.pageHeroService
      .getMediaUrl(this.mediaAssetId);
  }

  get fallbackUrl(): string | null {
    if (this.fallbackPreviewUrl) {
      return this.fallbackPreviewUrl;
    }

    return this.pageHeroService
      .getMediaUrl(
        this.fallbackMediaAssetId
      );
  }

  private isValidBackgroundFile(
    file: File
  ): boolean {
    return this.backgroundType === 'Video'
      ? file.type === 'video/mp4'
      : this.isValidImage(file);
  }

  private checkFileSize(file: File, isVideo: boolean): boolean {
    const maxBytes = (isVideo ? 25 : 5) * 1024 * 1024;
    if (file.size <= maxBytes) return true;
    this.toastService.error(isVideo
      ? 'Video must be 25 MB or smaller.'
      : 'Image must be 5 MB or smaller.');
    return false;
  }

  private isValidImage(file: File): boolean {
    return [
      'image/jpeg',
      'image/png',
      'image/webp'
    ].includes(file.type);
  }

  private clearBackgroundPreview(): void {
    if (this.backgroundPreviewUrl) {
      URL.revokeObjectURL(
        this.backgroundPreviewUrl
      );

      this.backgroundPreviewUrl = null;
    }
  }

  private clearFallbackPreview(): void {
    if (this.fallbackPreviewUrl) {
      URL.revokeObjectURL(
        this.fallbackPreviewUrl
      );

      this.fallbackPreviewUrl = null;
    }
  }
}
